import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAppSettings } from '../../context/AppSettingsContext';
import PatientProfileCard from './PatientProfileCard';
import { submitAppReview } from '../../services/patientService';
import toast from 'react-hot-toast';

// Convert any YouTube link to embeddable src
function toEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}?autoplay=1&rel=0`;
    }
    const v = u.searchParams.get('v');
    if (v) return `https://www.youtube.com/embed/${v}?autoplay=1&rel=0`;
    return null;
  } catch { return null; }
}

function VideoModal({ url, title, onClose }) {
  const embedUrl = toEmbedUrl(url);
  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 flex flex-col items-center justify-center px-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-black rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 bg-surface border-b border-border">
          <span className="text-sm font-bold text-text-primary">{title}</span>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white border border-border flex items-center justify-center">
            <i className="fa-solid fa-xmark text-text-secondary" />
          </button>
        </div>
        {embedUrl ? (
          <div className="relative" style={{ paddingBottom: '56.25%' }}>
            <iframe src={embedUrl} title={title} className="absolute inset-0 w-full h-full"
              allow="autoplay; fullscreen; encrypted-media" allowFullScreen />
          </div>
        ) : (
          <div className="p-6 text-center bg-white">
            <p className="text-sm text-text-secondary mb-4">This tutorial opens in YouTube.</p>
            <a href={url} target="_blank" rel="noopener noreferrer" className="btn-primary inline-block" onClick={onClose}>
              <i className="fa-brands fa-youtube mr-2" />Open on YouTube
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

// ── App Review Modal ──────────────────────────────────────────────────────
function AppReviewModal({ user, onClose }) {
  const [rating,  setRating]  = useState(0);
  const [hover,   setHover]   = useState(0);
  const [comment, setComment] = useState('');
  const [name,    setName]    = useState(user?.name || '');
  const [loading, setLoading] = useState(false);
  const [done,    setDone]    = useState(false);

  const handleSubmit = async () => {
    if (!rating)          { toast.error('Please select a rating'); return; }
    if (!comment.trim())  { toast.error('Please write your experience'); return; }
    setLoading(true);
    try {
      await submitAppReview({ name: name || 'Anonymous', rating, comment });
      setDone(true);
    } catch (err) {
      toast.error(err.message || 'Could not submit review');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-end justify-center" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-t-3xl p-5 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto" />

        {done ? (
          <div className="flex flex-col items-center py-8 gap-4 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <i className="fa-solid fa-star text-amber-400 text-2xl" />
            </div>
            <div>
              <p className="text-lg font-bold text-text-primary">Thank you!</p>
              <p className="text-sm text-text-muted mt-1">Your review is now live on the homepage!</p>
            </div>
            <button onClick={onClose} className="btn-primary px-8">Done</button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-text-primary">Rate Faristaa</h3>
                <p className="text-xs text-text-muted">Your review appears live on the homepage</p>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-xl bg-surface border border-border flex items-center justify-center">
                <i className="fa-solid fa-xmark text-text-secondary text-sm" />
              </button>
            </div>

            {/* Star rating */}
            <div className="flex justify-center gap-2 py-2">
              {[1,2,3,4,5].map(star => (
                <button key={star}
                  onMouseEnter={() => setHover(star)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(star)}
                  className="text-4xl transition-transform active:scale-90">
                  <i className={`fa-star ${(hover || rating) >= star ? 'fa-solid text-amber-400' : 'fa-regular text-gray-300'}`} />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-center text-sm font-semibold text-amber-600">
                {['','Poor','Fair','Good','Very Good','Excellent'][rating]}
              </p>
            )}

            {/* Name */}
            <div>
              <label className="input-label">Your Name (optional)</label>
              <input value={name} onChange={e => setName(e.target.value)}
                placeholder="Anonymous" className="input" maxLength={60} />
            </div>

            {/* Comment */}
            <div>
              <label className="input-label">Your Experience <span className="text-danger">*</span></label>
              <textarea value={comment} onChange={e => setComment(e.target.value)}
                rows={4} maxLength={600} placeholder="Tell us what you love or what we can improve..."
                className="input resize-none" />
              <p className="text-[10px] text-text-muted text-right mt-1">{comment.length}/600</p>
            </div>

            <button onClick={handleSubmit} disabled={loading || !rating || !comment.trim()}
              className="btn-primary w-full">
              {loading
                ? <><i className="fa-solid fa-spinner animate-spin mr-2" />Submitting...</>
                : <><i className="fa-solid fa-paper-plane mr-2" />Submit Review</>
              }
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Sidebar({ isOpen, onClose }) {
  const navigate  = useNavigate();
  const settings  = useAppSettings();
  const { isLoggedIn, logout, user, role } = useAuth();
  const [videoModal,  setVideoModal]  = useState(null);
  const [showReview,  setShowReview]  = useState(false);

  if (!isOpen) return null;

  const whatsappHref = settings.whatsappSupportNumber
    ? `https://wa.me/91${settings.whatsappSupportNumber}` : null;
  const callHref = settings.callSupportNumber
    ? `tel:${settings.callSupportNumber}` : null;

  // Tutorial links from admin
  const patientTutUrl = settings.patientTutorialUrl || settings.tutorialsUrl || null;
  const doctorTutUrl  = settings.doctorTutorialUrl  || settings.tutorialsUrl || null;

  const menuItems = [
    { icon:'fa-brands fa-whatsapp', label:'WhatsApp Support', color:'text-green-500', bg:'bg-green-50', href: whatsappHref },
    { icon:'fa-solid fa-phone',     label:'Call Support',     color:'text-primary',   bg:'bg-primary-light', href: callHref },
  ];

  // Tutorial items — show relevant one based on role, or both if not logged in
  const tutorialItems = [];
  if (role === 'patient' || !isLoggedIn) {
    tutorialItems.push({
      icon: 'fa-solid fa-circle-play',
      label: 'Tutorial for Patients',
      color: 'text-accent',
      bg: 'bg-amber-50',
      url: patientTutUrl,
    });
  }
  if (role === 'doctor' || role === 'staff' || !isLoggedIn) {
    tutorialItems.push({
      icon: 'fa-solid fa-play-circle',
      label: 'Tutorial for Doctors',
      color: 'text-purple-500',
      bg: 'bg-purple-50',
      url: doctorTutUrl,
    });
  }

  const handleTutorialClick = (item) => {
    if (!item.url) return;
    const embed = toEmbedUrl(item.url);
    if (embed) {
      setVideoModal({ url: item.url, title: item.label });
    } else {
      window.open(item.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <aside className="fixed top-0 left-0 bottom-0 w-72 bg-white z-50 shadow-xl flex flex-col overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-border bg-gradient-to-r from-primary/5 to-teal-50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-house-medical text-white text-sm" />
            </div>
            <span className="text-lg font-black text-primary font-heading tracking-tight">
              {settings.appName || 'Faristaa'}
            </span>
          </div>
          <button onClick={onClose}
            className="w-9 h-9 rounded-xl bg-surface flex items-center justify-center active:bg-slate-100">
            <i className="fa-solid fa-xmark text-text-secondary text-base" />
          </button>
        </div>

        {/* Patient Profile Card */}
        {isLoggedIn && role === 'patient' && user && (
          <div className="pt-3">
            <PatientProfileCard />
          </div>
        )}

        {/* Contact & Help */}
        <div className="px-4 pt-4">
          <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-3 px-1">Contact &amp; Help</p>
          <div className="space-y-1">
            {menuItems.map(item => {
              const inner = (
                <div className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-surface active:bg-surface transition-colors text-left">
                  <div className={`w-9 h-9 rounded-xl ${item.bg} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${item.icon} ${item.color} text-base`} />
                  </div>
                  <span className="text-sm font-medium text-text-primary">{item.label}</span>
                  {item.href
                    ? <i className="fa-solid fa-arrow-up-right-from-square text-text-muted text-[10px] ml-auto" />
                    : <span className="ml-auto text-[9px] text-text-muted bg-surface px-1.5 py-0.5 rounded-full">Not set</span>}
                </div>
              );
              if (item.href) {
                return (
                  <a key={item.label} href={item.href}
                    target={item.href.startsWith('http') ? '_blank' : '_self'}
                    rel="noopener noreferrer" onClick={onClose}>
                    {inner}
                  </a>
                );
              }
              return <div key={item.label} className="opacity-50 cursor-not-allowed">{inner}</div>;
            })}
          </div>
        </div>

        {/* Tutorials */}
        {tutorialItems.length > 0 && (
          <div className="px-4 pt-4">
            <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-3 px-1">Tutorials</p>
            <div className="space-y-1">
              {tutorialItems.map(item => (
                <button
                  key={item.label}
                  onClick={() => item.url ? handleTutorialClick(item) : null}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-surface active:bg-surface transition-colors text-left ${!item.url ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className={`w-9 h-9 rounded-xl ${item.bg} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${item.icon} ${item.color} text-base`} />
                  </div>
                  <span className="text-sm font-medium text-text-primary flex-1">{item.label}</span>
                  {item.url
                    ? <i className="fa-solid fa-play text-text-muted text-[10px]" />
                    : <span className="text-[9px] text-text-muted bg-surface px-1.5 py-0.5 rounded-full">Not set</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Rate our App */}
        <div className="px-4 pt-4">
          <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-3 px-1">What Patients Say</p>
          <button
            onClick={() => { setShowReview(true); }}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl bg-amber-50 border border-amber-200 hover:bg-amber-100 active:bg-amber-100 transition-colors text-left"
          >
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-star text-amber-400 text-base" />
            </div>
            <div className="flex-1">
              <span className="text-sm font-semibold text-amber-800">Rate Faristaa</span>
              <p className="text-[11px] text-amber-600 mt-0.5">Share your experience</p>
            </div>
            <div className="flex">
              {[1,2,3,4,5].map(s => (
                <i key={s} className="fa-solid fa-star text-amber-300 text-[10px]" />
              ))}
            </div>
          </button>
        </div>

        <div className="flex-1" />

        {/* Bottom section */}
        <div className="px-4 pb-6 pt-3 border-t border-border space-y-3">
          {isLoggedIn ? (
            <button
              onClick={() => { onClose(); logout(); navigate('/', { replace: true }); }}
              className="w-full flex items-center gap-3 bg-red-50 border border-red-200 text-danger px-4 py-3 rounded-2xl font-semibold text-sm active:scale-98 transition-transform">
              <div className="w-8 h-8 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="fa-solid fa-right-from-bracket text-danger text-sm" />
              </div>
              <span className="text-sm font-bold text-danger">Logout</span>
            </button>
          ) : (
            <button
              onClick={() => { sessionStorage.setItem('loginOrigin', window.location.pathname); onClose(); navigate('/login'); }}
              className="w-full flex items-center gap-3 bg-primary text-white px-4 py-3 rounded-2xl font-semibold text-sm shadow-lg shadow-primary/20 active:scale-98 transition-transform">
              <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="fa-solid fa-right-to-bracket text-white text-sm" />
              </div>
              <div className="text-left">
                <p className="text-sm font-bold">Login</p>
                <p className="text-[10px] text-white/80">Sign in to book appointments</p>
              </div>
            </button>
          )}

          <button
            onClick={() => { onClose(); navigate('/doctor/login'); }}
            className="w-full flex items-center gap-3 bg-teal-50 border border-teal-200 text-primary px-4 py-3 rounded-2xl font-semibold text-sm active:scale-98 transition-transform">
            <div className="w-8 h-8 bg-primary-light rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-user-doctor text-primary text-sm" />
            </div>
            <div className="text-left">
              <p className="text-sm font-bold text-primary">Are you a Doctor?</p>
              <p className="text-[10px] text-primary/70">Join Faristaa — it&apos;s free</p>
            </div>
            <i className="fa-solid fa-arrow-right ml-auto text-primary/50 text-xs" />
          </button>
        </div>
      </aside>

      {/* Video Modal */}
      {videoModal && (
        <VideoModal
          url={videoModal.url}
          title={videoModal.title}
          onClose={() => setVideoModal(null)}
        />
      )}

      {/* App Review Modal */}
      {showReview && (
        <AppReviewModal
          user={user}
          onClose={() => setShowReview(false)}
        />
      )}
    </>
  );
}
